import { Ref, useCallback, useEffect, useRef } from "react";
import { useBlocker } from "react-router";

import { Button } from "@/components/ui";
import { CopyButton } from "@/components/ui/CopyButton";
import { headingVariants } from "@/components/ui/Heading";
import { useAuthContext } from "@/context/auth";
import { fileNameDate, formatDate } from "@/lib/format";

/**
 * The file outlives this page, so it says whose codes they are and when they were issued: someone
 * who finds it months later can tell which account it belongs to and whether it is the current set.
 */
const fileContent = (codes: string[], account: string | undefined) =>
  [
    "SkillPanel backup codes",
    ...(account ? [`Account: ${account}`] : []),
    `Created: ${formatDate(new Date())}`,
    "",
    "Each code works for a single sign-in. Keep this file somewhere safe.",
    "",
    ...codes,
    "",
  ].join("\n");

const download = (codes: string[], account: string | undefined) => {
  const blob = new Blob([fileContent(codes, account)], { type: "text/plain" });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `skillpanel-backup-codes-${fileNameDate()}.txt`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

/**
 * The codes are shown exactly once, so leaving this panel without saving them is unrecoverable.
 * `beforeunload` covers refresh, tab close and cross-origin navigation; the router `useBlocker`
 * covers in-app navigation, including the browser Back button. The two guards cover disjoint
 * exits, so both are kept.
 *
 * Both guards read `acknowledgedRef` rather than component state. `onAcknowledge` is free to
 * trigger a `window.location.href` assignment or a router `navigate()` call synchronously, and
 * both race React's render cycle - a `useState` flag flipped in the same handler is not
 * guaranteed to be visible to the `beforeunload` listener, or to the blocker predicate, by the
 * time either runs. The ref is set before `onAcknowledge()` is invoked, so both guards always see
 * the current value regardless of whether React has re-rendered yet.
 */
export const BackupCodesPanel = (props: {
  codes: string[];
  onAcknowledge: () => void;
  acknowledgeLabel: string;
  /** For a caller that moves focus to the panel when the codes appear */
  ref?: Ref<HTMLElement>;
}) => {
  const { codes, onAcknowledge, acknowledgeLabel, ref } = props;
  const { user } = useAuthContext();

  const acknowledgedRef = useRef(false);

  useEffect(() => {
    const warnBeforeLeaving = (event: BeforeUnloadEvent) => {
      if (!acknowledgedRef.current) {
        event.preventDefault();
      }
    };
    window.addEventListener("beforeunload", warnBeforeLeaving);
    return () => window.removeEventListener("beforeunload", warnBeforeLeaving);
  }, []);

  // The predicate form re-runs on every navigation attempt and reads the ref directly, so it
  // stays correct without ever needing to re-register with the router.
  const blocker = useBlocker(useCallback(() => !acknowledgedRef.current, []));

  // The leave guard appears in the middle of the page, so focus goes into it - on the safe choice -
  // rather than staying on whatever control triggered the navigation. Staying hands focus back to
  // the acknowledge button, as the guard unmounts.
  const stayRef = useRef<HTMLButtonElement>(null);
  const acknowledgeRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (blocker.state === "blocked") {
      stayRef.current?.focus();
    }
  }, [blocker.state]);

  const acknowledge = () => {
    acknowledgedRef.current = true;
    onAcknowledge();
  };

  return (
    <section
      ref={ref}
      tabIndex={-1}
      aria-labelledby="backup-codes-heading"
      className="space-y-6 focus-visible:outline-hidden"
    >
      <div className="space-y-2">
        <h2 id="backup-codes-heading" className={headingVariants({ variant: "title" })}>
          Save your backup codes
        </h2>
        <p className="text-sm text-muted-foreground">
          These codes are shown only once, so save them somewhere safe before you leave this page.
          Each code works for a single sign-in.
        </p>
      </div>

      {blocker.state === "blocked" && (
        <div
          role="alertdialog"
          aria-labelledby="leave-heading"
          className="space-y-3 rounded-xl border border-destructive-border bg-destructive-wash p-4"
        >
          <p id="leave-heading" className="text-sm font-semibold text-destructive-fg">
            You haven't confirmed you saved your backup codes. If you leave now, they're lost.
          </p>
          <div className="flex flex-wrap gap-3">
            <Button
              ref={stayRef}
              type="button"
              variant="brand"
              size="xl"
              onClick={() => {
                blocker.reset();
                acknowledgeRef.current?.focus();
              }}
            >
              Stay on this page
            </Button>
            <Button type="button" size="xl" variant="destructive" onClick={() => blocker.proceed()}>
              Leave without saving
            </Button>
          </div>
        </div>
      )}

      {/* The copy button sits on the block it copies, as on a code sample */}
      <div className="relative rounded-xl bg-muted p-4 pr-14">
        <ul className="grid grid-cols-2 gap-x-4 gap-y-1 font-mono text-sm">
          {codes.map((code) => (
            <li key={code}>{code}</li>
          ))}
        </ul>
        <CopyButton
          label="backup codes"
          value={codes.join("\n")}
          className="absolute right-2 top-2 size-9 pointer-coarse:size-11"
        />
      </div>

      <div className="space-y-3">
        <Button
          type="button"
          size="xl"
          variant="outline"
          className="w-full"
          onClick={() => download(codes, user?.username)}
        >
          Download codes
        </Button>
        <Button
          ref={acknowledgeRef}
          type="button"
          variant="brand"
          size="xl"
          className="w-full"
          onClick={acknowledge}
        >
          {acknowledgeLabel}
        </Button>
      </div>
    </section>
  );
};
